import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, UserIcon } from '@heroicons/react/24/outline';

const BlogPreview = ({ posts = [], isLoading = false }) => {
  const [imageErrors, setImageErrors] = useState({});

  // Format date to readable format
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Handle image error
  const handleImageError = (postId) => {
    setImageErrors(prev => ({
      ...prev,
      [postId]: true
    }));
  };

  // Get fallback image based on category
  const getFallbackImage = (category) => {
    const fallbacks = {
      'PHOTOGRAPHY': 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'TRAVEL TIPS': 'https://images.unsplash.com/photo-1501555088652-021faa106b9b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'PLANNING': 'https://images.unsplash.com/photo-1517842645767-c639042777db?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'WILDLIFE': 'https://images.unsplash.com/photo-1504173010664-32509aeebb62?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'ADVENTURE': 'https://images.unsplash.com/photo-1527631746610-bca00a040d60?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'default': 'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80'
    };
    
    return fallbacks[category?.toUpperCase()] || fallbacks.default;
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
            <div className="h-48 bg-gray-300"></div>
            <div className="p-6">
              <div className="h-6 bg-gray-300 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-gray-300 rounded w-full mb-4"></div>
              <div className="h-4 bg-gray-300 rounded w-full mb-4"></div>
              <div className="flex justify-between">
                <div className="h-4 bg-gray-300 rounded w-1/3"></div>
                <div className="h-4 bg-gray-300 rounded w-1/3"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Sample blog posts for demonstration
  const samplePosts = [
    {
      id: 1,
      title: "Top 10 Wildlife Photography Tips",
      excerpt: "Capture stunning wildlife moments with these expert photography techniques.",
      image: "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80",
      date: "2023-06-15",
      author: "Michael Roberts",
      category: "PHOTOGRAPHY",
      slug: "wildlife-photography-tips"
    },
    {
      id: 2,
      title: "Essential Packing Guide for Safari Adventures",
      excerpt: "Everything you need to bring for a comfortable and memorable safari experience.",
      image: "https://images.unsplash.com/photo-1501555088652-021faa106b9b?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80",
      date: "2023-05-22",
      author: "Lisa Thompson",
      category: "TRAVEL TIPS",
      slug: "safari-packing-guide"
    },
    {
      id: 3,
      title: "Best Season to Visit East African National Parks",
      excerpt: "Plan your trip during the optimal seasons for wildlife viewing and comfortable weather.",
      image: "https://images.unsplash.com/photo-1517842645767-c639042777db?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80",
      date: "2023-04-10",
      author: "James Kimani",
      category: "PLANNING",
      slug: "best-season-east-africa"
    }
  ];

  // No posts available - use sample posts instead of showing "no posts" message
  if (!posts || posts.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 blog-posts-grid">
        {samplePosts.map((post) => (
          <Link 
            key={post.id} 
            to={post.slug ? `/blog/${post.slug}` : '/blog'}
            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 group blog-post-card"
          >
            {/* Post Image */}
            <div className="relative h-48 overflow-hidden bg-gray-200">
              <img 
                src={imageErrors[post.id] ? getFallbackImage(post.category) : post.image} 
                alt={post.title} 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onError={() => handleImageError(post.id)}
                loading="eager"
              />
              {post.category && (
                <div className="absolute top-4 left-4 bg-primary text-white text-xs uppercase tracking-wider py-1 px-2 rounded">
                  {post.category}
                </div>
              )}
            </div>
            
            {/* Post Content */}
            <div className="p-6">
              <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-200">
                {post.title}
              </h3>
              <p className="text-gray-600 mb-4 line-clamp-3">
                {post.excerpt}
              </p>
              
              {/* Post Meta */}
              <div className="flex justify-between text-sm text-gray-500">
                <div className="flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-1" />
                  <span>{formatDate(post.date)}</span>
                </div>
                <div className="flex items-center">
                  <UserIcon className="h-4 w-4 mr-1" />
                  <span>{post.author}</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 blog-posts-grid">
      {posts.map((post) => (
        <Link 
          key={post.id} 
          to={post.slug ? `/blog/${post.slug}` : '/blog'}
          className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 group blog-post-card"
        >
          {/* Post Image */}
          <div className="relative h-48 overflow-hidden bg-gray-200">
            <img 
              src={imageErrors[post.id] ? getFallbackImage(post.category) : (post.image || getFallbackImage(post.category))} 
              alt={post.title} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={() => handleImageError(post.id)}
              loading="eager"
            />
            {post.category && (
              <div className="absolute top-4 left-4 bg-primary text-white text-xs uppercase tracking-wider py-1 px-2 rounded">
                {post.category}
              </div>
            )}
          </div>
          
          {/* Post Content */}
          <div className="p-6">
            <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-200">
              {post.title}
            </h3>
            <p className="text-gray-600 mb-4 line-clamp-3">
              {post.excerpt}
            </p>
            
            {/* Post Meta */}
            <div className="flex justify-between text-sm text-gray-500">
              <div className="flex items-center">
                <CalendarIcon className="h-4 w-4 mr-1" />
                <span>{formatDate(post.date)}</span>
              </div>
              <div className="flex items-center">
                <UserIcon className="h-4 w-4 mr-1" />
                <span>{post.author}</span>
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default BlogPreview;
